/**
 * The tool-suggestion writer names 1-2 of the (up to 10) search results in its prose, as
 * markdown links whose target is the result's verbatim URL. The card is otherwise rendered in
 * the backend's ranking order, so a named result can sit far down the list. These helpers float
 * the named results to the top of the card while preserving the relative order within each group.
 */

const MARKDOWN_LINK_TARGET = /\]\(([^)\s]+)/g;

/**
 * Extracts the URL target of every markdown link in the prose. Handles optional link titles
 * (`[text](url "title")`) by stopping at the first whitespace.
 */
export function extractMentionedUrls(content: string): Set<string> {
    const urls = new Set<string>();
    for (const match of content.matchAll(MARKDOWN_LINK_TARGET)) {
        urls.add(match[1]);
    }
    return urls;
}

/**
 * Returns a new list with results whose URL appears in the prose moved to the front, in their
 * original relative order; the rest follow, also in original order. Returns the input unchanged
 * when there is no prose or nothing matches, so non-tool-suggestion callers are unaffected.
 */
export function reorderResultsByMention<T extends {url: string}>(
    results: T[],
    messageContent?: string
): T[] {
    if (!messageContent) return results;

    const mentioned = extractMentionedUrls(messageContent);
    if (mentioned.size === 0) return results;

    const named: T[] = [];
    const rest: T[] = [];
    for (const result of results) {
        if (result.url && mentioned.has(result.url)) {
            named.push(result);
        } else {
            rest.push(result);
        }
    }

    return named.length > 0 ? [...named, ...rest] : results;
}
